import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Check, ChevronDown, Search, UserPlus, X } from 'lucide-react';
import { ticketsApi } from '../../api/tickets';
import { companiesApi } from '../../api/companies';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useAnchoredMenu } from '../../hooks/useAnchoredMenu';

const STAFF_ROLES = ['technician', 'admin', 'owner'];
const MENU_WIDTH = 264;

interface Props {
  ticketId: string;
  /** Current primary assignee id, if any. */
  assignedToId?: string | null;
  assignedToName?: string | null;
  /** Invalidates the ticket + any list that shows the assignee. */
  onAssigned?: () => void;
  /** `sidebar` (default) assigns on click. `compose` only stages the pick for
   *  the reply box, which sends it with the message so the whole update goes
   *  out as one email per person. */
  variant?: 'sidebar' | 'compose';
  /** compose: the staged pick — undefined = no change, null = unassign. */
  staged?: { id: string | null; name: string | null };
  onStage?: (pick: { id: string | null; name: string | null } | undefined) => void;
  disabled?: boolean;
}

/**
 * One-click reassignment from the ticket sidebar. Assigning used to mean
 * Edit → Assignment tab → picker → Save; this is the same change in one
 * click, with "Assign to me" first because that's the common case.
 *
 * Only the primary assignee is set here — co-assignees and teams stay in the
 * edit modal, and are left untouched (the API only replaces co-assignees when
 * the field is actually sent).
 */
export default function QuickAssign({
  ticketId, assignedToId, assignedToName, onAssigned, variant = 'sidebar', staged, onStage, disabled,
}: Props) {
  const isCompose = variant === 'compose';
  const qc = useQueryClient();
  const { user } = useAuth();
  const { currentCompany } = useCompany();
  const [search, setSearch] = useState('');
  const { open, setOpen, triggerRef, menuRef, style } = useAnchoredMenu({ width: MENU_WIDTH, align: isCompose ? 'left' : 'right' });

  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const myId = (user as any)?.id;

  const { data: members = [] } = useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () => companiesApi.getMembers(companyId),
    enabled: !!companyId && open,   // only fetched once the menu is actually opened
  });

  const mutation = useMutation({
    mutationFn: (userId: string | null) =>
      ticketsApi.update(ticketId, { assignedToId: userId } as any),
    onSuccess: (_, userId) => {
      setOpen(false);
      setSearch('');
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket-activity', ticketId] });
      onAssigned?.();
      const name = userId
        ? (userId === myId ? 'you' : nameOf(userId))
        : null;
      toast.success(name ? `Assigned to ${name}` : 'Unassigned');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const nameOf = (id: string) => {
    const m = (members as any[]).find((m: any) => m.user?.id === id);
    return m?.user?.name ?? m?.user?.email ?? 'Unknown';
  };

  const staff = (members as any[]).filter((m: any) => STAFF_ROLES.includes(m.role));
  const filtered = staff.filter((m: any) => {
    const name = `${m.user?.name ?? ''} ${m.user?.email ?? ''}`;
    return name.toLowerCase().includes(search.toLowerCase());
  });

  // What the menu treats as "current": the staged pick in the reply box.
  const selectedId = isCompose && staged ? staged.id : (assignedToId ?? null);
  const assignedToMe = !!myId && selectedId === myId;

  const pick = (userId: string | null) => {
    if (!isCompose) { mutation.mutate(userId); return; }
    setOpen(false);
    setSearch('');
    // Picking the ticket's current assignee again just drops the staged change.
    if (userId === (assignedToId ?? null)) onStage?.(undefined);
    else onStage?.({ id: userId, name: userId ? (userId === myId ? 'you' : nameOf(userId)) : null });
  };
  const hasStaged = isCompose && !!staged && staged.id !== (assignedToId ?? null);

  return (
    <>
      {isCompose ? (
        <span className="relative inline-flex items-center">
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen(!open)}
            disabled={disabled}
            className="btn btn-ghost text-xs py-1.5 gap-1.5"
            aria-haspopup="listbox"
            aria-expanded={open}
            title="Reassign the ticket when you send this update"
          >
            <span className="text-muted font-medium">Assignee:</span>
            <span className="font-bold">{assignedToName ?? 'Unassigned'}</span>
            {hasStaged && (
              <>
                <span className="text-muted">→</span>
                <span className="font-bold text-primary">{staged!.name ?? 'Unassigned'}</span>
              </>
            )}
            <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {hasStaged && (
            <button
              type="button"
              onClick={() => onStage?.(undefined)}
              className="ml-1 text-muted hover:text-err"
              title="Keep the current assignee"
              aria-label="Clear staged reassignment"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </span>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen(!open)}
          disabled={mutation.isPending}
          className="inline-flex items-center gap-1 text-xs font-medium text-fg hover:text-primary transition-colors"
          aria-haspopup="listbox"
          aria-expanded={open}
          title="Change assignee"
        >
          {mutation.isPending ? 'Saving…' : (assignedToName ?? 'Unassigned')}
          <ChevronDown className={`w-3 h-3 text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      )}

      {open && style && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          className="bg-surface border border-line rounded-lg shadow-xl flex flex-col overflow-hidden"
          style={style}
        >
          {/* Fast paths first — taking a ticket is the common move. */}
          <div className="p-1 border-b border-line-light shrink-0">
            {myId && !assignedToMe && (
              <button
                type="button"
                onClick={() => pick(myId)}
                className="w-full px-2.5 py-2 text-sm text-left rounded-md hover:bg-row-odd flex items-center gap-2 text-primary font-medium"
              >
                <UserPlus className="w-3.5 h-3.5" /> Assign to me
              </button>
            )}
            {selectedId && (
              <button
                type="button"
                onClick={() => pick(null)}
                className="w-full px-2.5 py-2 text-sm text-left rounded-md hover:bg-row-odd text-muted"
              >
                Unassign
              </button>
            )}
            <div className="flex items-center gap-2 px-2 py-1 mt-1 bg-row-odd rounded-md">
              <Search className="w-3.5 h-3.5 text-muted shrink-0" />
              <input
                autoFocus
                className="flex-1 min-w-0 text-sm bg-transparent outline-none text-fg placeholder:text-muted"
                placeholder="Search staff…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="overflow-y-auto py-1">
            {filtered.map((m: any) => {
              const uid = m.user?.id;
              const name = m.user?.name ?? m.user?.email;
              const selected = uid === selectedId;
              return (
                <button
                  key={uid}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => pick(uid)}
                  className="w-full px-3 py-2 text-sm text-left hover:bg-row-odd flex items-center gap-2"
                >
                  <span className="w-4 flex items-center justify-center shrink-0">
                    {selected && <Check className="w-3.5 h-3.5 text-primary" />}
                  </span>
                  <span className="w-6 h-6 rounded-full brand-gradient flex items-center justify-center text-white text-[10px] font-semibold shrink-0">
                    {(name ?? '?')[0].toUpperCase()}
                  </span>
                  <span className="text-fg truncate">{name}</span>
                  <span className="ml-auto text-[11px] text-muted capitalize shrink-0">{m.role}</span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted">
                {members.length === 0 ? 'Loading…' : 'No staff found'}
              </p>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
